import * as Types from './types'

export const weekendLeagueRoundLabel = 'Week'
export const weekendLeagueDemoRedirectSeconds = 5
export const weekendLeagueNicknameMaxLength = 20
export const weekendLeagueNicknameMinLength = 3
export const weekendLeagueMarketsFallback = 'gold and silver, crypto or synthetic indices'
// The module still serves <TBD - OQ-P1-3> for termsByLanguage. The document itself is
// published, and is the same one the public landing page links, so link it rather than
// leaving the client with no way to read what they are accepting. A served value wins.
export const weekendLeagueTermsUrl =
    'https://www.thinkmarkets.com/getmedia/841c151c-64b2-4bd4-935b-50636409bfa7/'
    + 'ThinkMarkets-weekend-league-terms-and-conditions.pdf'

export const weekendLeagueClientPath = (segment: string) => `/clients/me/${segment}`

export type ModuleIneligibleReason =
    | 'not_tmlc'
    | 'wrong_platform'
    | 'demo_account'
    | 'account_status_ineligible'

export type ModuleMoney = { amount: number; currency: string }

export type CompetitionResponse = {
    name?: string
    startsOn?: string
    endsOn?: string
    platforms?: string[]
    totalPrizeValue?: ModuleMoney
    prizePlaces?: { place: number; amount: number; currency: string }[]
    marketGroups?: { key: string; name?: string; description?: string }[]
    rules?: { qualifyingTrades?: number; scoringMethod?: string }
    termsByLanguage?: Record<string, string>
    terms?: { version?: string; url?: string }
    nicknameMaxLength?: number
    nicknameMinLength?: number
}

export type EligibleAccountResponse = {
    accountId: string
    platform?: string
    currency?: string
    eligible?: boolean
    reasons?: ModuleIneligibleReason[]
    alreadyRegistered?: boolean
}

export type EligibleAccountsResponse = { accounts?: EligibleAccountResponse[] }

export type ClientRegistrationResponse = {
    accountId: string
    nickname: string
    effectiveRound?: number | null
    registeredAt?: string
}

export type ClientRegistrationsResponse = { registrations?: ClientRegistrationResponse[] }

export type ModuleWeekendState = 'coming_soon' | 'active' | 'completed'

export type PositionEntryResponse = {
    registeredAccountId: string
    nickname: string
    roundNumber: number
    rank?: number | null
    pctReturn?: number | null
    qualified?: boolean
    isFinal?: boolean
    confirmed?: boolean
}

export type PositionResponse = {
    positions?: PositionEntryResponse[]
    weekends?: { roundNumber: number; state: ModuleWeekendState }[]
}

export type RegisterOutcomeResponse = {
    accountId: string
    status?: 'registered' | 'already_registered' | 'rejected'
    effectiveRound?: number | null
    nickname?: string
    reasons?: ModuleIneligibleReason[]
    nicknameRejected?: boolean
    message?: string
}

export type RegisterResponse = { outcomes?: RegisterOutcomeResponse[] }

export type NicknameAvailabilityResponse = { available?: boolean }

export type WeekendsResponse = {
    defaultWeekend?: number
    weekends?: { number: number; startsOn?: string; endsOn?: string; state: ModuleWeekendState }[]
}

// The not_started shape of the standings route. It is the only place the module states
// the opening time as well as the date, which a countdown needs.
export type RoundStartResponse = {
    status?: string
    startsGmt?: string
    opensGmt?: string
}

export type WeekendLeagueResponses = {
    competition: CompetitionResponse | null
    eligibleAccounts: EligibleAccountsResponse | null
    registrations: ClientRegistrationsResponse | null
    position: PositionResponse | null
    // Optional: best effort, and absent in fixtures that predate the countdown.
    nextRound?: { roundNumber: number; startsGmt: string | null } | null
    // True while any round is open. The countdown is for the wait between rounds, so it
    // is suppressed while the competition is actually running.
    roundInProgress?: boolean
}

export const asPayload = <T>(result: T) =>
    ({ payload: [{ status: 'OK', result }] } as unknown as Types.APIResponse<T>)

export const toAccountNumber = (accountId: string): number => {
    const parsed = parseInt(accountId, 10)
    return isNaN(parsed) ? 0 : parsed
}

export const joinMarketNames = (names: string[]): string => {
    const usable = names.filter(name => !!name && name.indexOf('TBD') < 0)
    if (!usable.length) return weekendLeagueMarketsFallback
    if (usable.length === 1) return usable[0]

    return `${usable.slice(0, -1).join(', ')} or ${usable[usable.length - 1]}`
}

export const roundName = (roundNumber: number) => `${weekendLeagueRoundLabel} ${roundNumber}`

export const mapCompetition = (
    response: CompetitionResponse | null,
    language: string
): Types.WeekendLeagueCompetition | null => {
    if (!response || !response.name) return null

    const terms = response.termsByLanguage || {}
    const served = terms[language] || terms.en || response.terms?.url || ''
    const termsUrl = !served || served.indexOf('TBD') > -1 ? weekendLeagueTermsUrl : served

    return {
        competitionName: response.name,
        roundLabel: weekendLeagueRoundLabel,
        totalPrizePool: response.totalPrizeValue || { amount: 0, currency: '' },
        prizeTiers: (response.prizePlaces || []).map(place => ({
            position: place.place,
            amount: place.amount,
            currency: place.currency,
        })),
        eligibleMarketsSummary: joinMarketNames(
            (response.marketGroups || []).map(group => group.name || '')
        ),
        qualifyingTradeCount: response.rules?.qualifyingTrades || 0,
        nicknameMaxLength: response.nicknameMaxLength || weekendLeagueNicknameMaxLength,
        nicknameMinLength: response.nicknameMinLength || weekendLeagueNicknameMinLength,
        demoRedirectSeconds: weekendLeagueDemoRedirectSeconds,
        termsUrl,
        termsVersion: response.terms?.version || '',
    }
}

const reasonsOf = (accounts: EligibleAccountResponse[]): ModuleIneligibleReason[] =>
    accounts.reduce<ModuleIneligibleReason[]>((all, account) => all.concat(account.reasons || []), [])

const isRegistrable = (account: EligibleAccountResponse) =>
    account.eligible === true && account.alreadyRegistered !== true

export const resolveIneligibleReason = (
    accounts: EligibleAccountResponse[]
): Types.WeekendLeagueIneligibleReason | undefined => {
    const ineligible = accounts.filter(account => account.eligible !== true)
    if (!ineligible.length) return undefined

    const reasons = reasonsOf(ineligible)
    const everyAccountIsDemo = accounts.length > 0 && accounts.every(
        account => (account.reasons || []).indexOf('demo_account') > -1
    )

    if (everyAccountIsDemo) return 'NO_LIVE_ACCOUNT'
    if (reasons.indexOf('wrong_platform') > -1) return 'NO_ELIGIBLE_PLATFORM'
    if (reasons.indexOf('demo_account') > -1) return 'NO_LIVE_ACCOUNT'
    if (reasons.indexOf('not_tmlc') > -1) return 'ENTITY'

    return undefined
}

export const mapEligibility = (
    response: EligibleAccountsResponse | null
): Types.WeekendLeagueEligibility => {
    const accounts = response?.accounts

    // Entirely the module's decision. An unanswered read is not a licence to guess:
    // showing a competition to a client who cannot enter is the worse failure, and
    // the banner already depends on /competition answering.
    if (!accounts || !accounts.length) return { canSeeBanner: false, canCompete: false }

    const everyAccountIsWrongEntity = accounts.every(
        account => (account.reasons || []).indexOf('not_tmlc') > -1
    )
    if (everyAccountIsWrongEntity) {
        return { canSeeBanner: false, canCompete: false, ineligibleReason: 'ENTITY' }
    }

    const canCompete = accounts.some(isRegistrable)

    return {
        canSeeBanner: true,
        canCompete,
        ineligibleReason: canCompete ? undefined : resolveIneligibleReason(accounts),
    }
}

// "2026-08-27 08:00 GMT" as epoch ms. Parsed by hand rather than handed to
// new Date(string): that form is implementation defined, and IE 11 and Safari read it
// differently or not at all. Returns null for anything that is not exactly this shape.
export const parseGmtStamp = (value: string | null | undefined): number | null => {
    if (typeof value !== 'string') return null
    const parts = value.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/)
    if (!parts) return null

    const stamp = Date.UTC(
        Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]),
        Number(parts[4]), Number(parts[5]), Number(parts[6] || 0)
    )
    return isNaN(stamp) ? null : stamp
}

export const parseGmtDate = (value: string | null | undefined): number | null => {
    if (typeof value !== 'string') return null
    const parts = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (!parts) return null

    const stamp = Date.UTC(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]))
    return isNaN(stamp) ? null : stamp
}

const oneDayMs = 24 * 60 * 60 * 1000

export const isWithinCompetitionWindow = (
    response: CompetitionResponse | null,
    now: number = Date.now()
): boolean => {
    const startsAt = parseGmtDate(response?.startsOn)
    const endsAt = parseGmtDate(response?.endsOn)
    if (startsAt === null || endsAt === null) return false

    return now >= startsAt && now < endsAt + oneDayMs
}

// The first round still to open, which is what a countdown counts to. The module lists
// rounds in order but that is not promised, so this sorts before choosing.
export const isRoundInProgress = (response: WeekendsResponse | null): boolean =>
    (response?.weekends || []).some(weekend => weekend.state === 'active')

export const selectNextRound = (response: WeekendsResponse | null) => {
    const weekends = (response?.weekends || [])
        .slice()
        .sort((left, right) => left.number - right.number)

    return weekends.find(weekend => weekend.state === 'coming_soon') || null
}

export const selectCurrentWeekend = (response: PositionResponse | null) => {
    const weekends = (response?.weekends || [])
        .slice()
        .sort((left, right) => left.roundNumber - right.roundNumber)
    if (!weekends.length) return null

    return weekends.find(weekend => weekend.state === 'active')
        || weekends.find(weekend => weekend.state === 'coming_soon')
        || weekends[weekends.length - 1]
}

const earliestEffectiveRound = (registrations: ClientRegistrationResponse[]) =>
    registrations.reduce<number | null>((earliest, registration) => (
        typeof registration.effectiveRound === 'number'
        && (earliest === null || registration.effectiveRound < earliest)
            ? registration.effectiveRound
            : earliest
    ), null)

// A client's entry can begin after the competition's live round: registering during
// round 2 with effectiveRound 3 means there is nothing to trade for or be ranked in
// until round 3 opens. Reporting the live round in that case tells the client the
// competition is running for them when it is not, and asks for qualifying trades that
// cannot count. Once the live round reaches their effective round, the live round is
// theirs and stays theirs for every round after it.
export const selectClientWeekend = (
    position: PositionResponse | null,
    registrations: ClientRegistrationResponse[]
) => {
    const current = selectCurrentWeekend(position)
    const effective = earliestEffectiveRound(registrations)
    if (!current || effective === null || current.roundNumber >= effective) return current

    return (position?.weekends || []).find(weekend => weekend.roundNumber === effective)
        || { roundNumber: effective, state: 'coming_soon' as ModuleWeekendState }
}

export const mapMyStandings = (
    position: PositionResponse | null,
    registrations: ClientRegistrationResponse[]
): Types.WeekendLeagueMyStandings | null => {
    const current = selectClientWeekend(position, registrations)
    if (!current) return null

    const entries = (position?.positions || [])
        .filter(entry => entry.roundNumber === current.roundNumber)

    const accountIdByNickname = registrations.reduce<Record<string, string>>((all, registration) => {
        if (registration.nickname) all[registration.nickname] = registration.accountId
        return all
    }, {})

    const isConfirmed = entries.length > 0 && entries.every(entry => entry.confirmed === true)
    const roundState: Types.WeekendLeagueRoundState = current.state === 'active'
        ? 'RUNNING'
        : current.state === 'coming_soon'
            ? 'NOT_STARTED'
            : isConfirmed ? 'CONFIRMED' : 'ENDED'

    return {
        roundState,
        roundNumber: current.roundNumber,
        roundName: roundName(current.roundNumber),
        standings: entries.map(entry => {
            const accountId = accountIdByNickname[entry.nickname] || entry.registeredAccountId
            return {
                accountId,
                accountNumber: toAccountNumber(accountId),
                nickname: entry.nickname,
                rank: typeof entry.rank === 'number' ? entry.rank : undefined,
                qualified: entry.qualified === true,
            }
        }),
    }
}

// Every account the client has entered, in the order the module returns them, which is
// the order they registered. Independent of the standings: an entry exists as soon as it
// is accepted, whereas a standing needs a round the client is in and qualifying trades.
export const mapEnrolledAccounts = (
    registrations: ClientRegistrationResponse[]
): Types.WeekendLeagueEnrolledAccount[] => registrations.map(registration => ({
    accountId: registration.accountId,
    accountNumber: toAccountNumber(registration.accountId),
    nickname: registration.nickname || '',
    roundNumber: typeof registration.effectiveRound === 'number'
        ? registration.effectiveRound
        : undefined,
}))

export const mapBannerContext = (
    responses: WeekendLeagueResponses,
    language: string
): Types.WeekendLeagueBannerContext => {
    const competition = mapCompetition(responses.competition, language)
    const eligibility = mapEligibility(responses.eligibleAccounts)
    const registrations = responses.registrations?.registrations || []
    const isRegistered = registrations.length > 0
    const accounts = responses.eligibleAccounts?.accounts || []

    return {
        competition,
        eligibility,
        isRegistered,
        hasUnregisteredEligibleAccounts: accounts.some(isRegistrable),
        myStandings: isRegistered ? mapMyStandings(responses.position, registrations) : null,
        enrolledAccounts: isRegistered ? mapEnrolledAccounts(registrations) : [],
        nextRoundStartsAt: responses.roundInProgress
            ? null
            : parseGmtStamp(responses.nextRound?.startsGmt),
    }
}

export const resolveEmptyReason = (
    accounts: EligibleAccountResponse[]
): Types.WeekendLeagueEmptyReason => {
    const eligible = accounts.filter(account => account.eligible === true)
    if (eligible.length && eligible.every(account => account.alreadyRegistered === true)) {
        return 'ALL_REGISTERED'
    }
    if (accounts.length && accounts.every(
        account => (account.reasons || []).indexOf('demo_account') > -1
    )) {
        return 'NO_LIVE_ACCOUNTS'
    }
    return 'NONE_ELIGIBLE'
}

export const mapRegistrableAccounts = (
    response: EligibleAccountsResponse | null
): Types.WeekendLeagueRegistrableAccountsResponse => {
    const accounts = response?.accounts || []
    const registrable = accounts.filter(isRegistrable)

    if (!registrable.length) return { accounts: [], emptyReason: resolveEmptyReason(accounts) }

    return {
        accounts: registrable.map(account => ({
            accountId: account.accountId,
            accountNumber: toAccountNumber(account.accountId),
            currency: account.currency || '',
            platform: account.platform || '',
        })),
    }
}

export const buildRegisterBody = (params: Types.WeekendLeagueRegisterParams) => ({
    items: params.entries.map(entry => ({
        accountId: entry.accountId,
        nickname: entry.nickname,
    })),
    terms: {
        version: params.termsVersion,
        language: params.language,
    },
    permanenceAcknowledged: params.permanenceAcknowledged,
})

const outcomeAccepted = (outcome: RegisterOutcomeResponse) =>
    outcome.status === 'registered' || outcome.status === 'already_registered'

const outcomeRejectionCode = (
    outcome: RegisterOutcomeResponse
): Types.WeekendLeagueEntryRejectionCode | undefined => {
    if (outcome.nicknameRejected === true) return 'NICKNAME_TAKEN'
    if ((outcome.reasons || []).length) return 'ACCOUNT_INELIGIBLE'
    return undefined
}

export const mapRegisterResponse = (
    response: RegisterResponse | null
): Types.WeekendLeagueRegisterResponse => {
    const outcomes = response?.outcomes || []
    const effectiveRound = outcomes
        .filter(outcomeAccepted)
        .reduce<number | null>(
            (found, outcome) => (found === null && typeof outcome.effectiveRound === 'number'
                ? outcome.effectiveRound
                : found),
            null
        )

    return {
        effectiveFromRoundNumber: effectiveRound || 0,
        effectiveFromRoundName: effectiveRound === null ? '' : roundName(effectiveRound),
        entries: outcomes.map(outcome => ({
            accountId: outcome.accountId,
            nickname: outcome.nickname || '',
            accepted: outcomeAccepted(outcome),
            rejectionCode: outcomeAccepted(outcome) ? undefined : outcomeRejectionCode(outcome),
        })),
    }
}

export const mapTermsRejection = (
    entries: Types.WeekendLeagueRegistrationEntry[]
): Types.WeekendLeagueRegisterResponse => ({
    effectiveFromRoundNumber: 0,
    effectiveFromRoundName: '',
    entries: entries.map(entry => ({
        accountId: entry.accountId,
        nickname: entry.nickname,
        accepted: false,
        rejectionCode: 'TERMS_NOT_ACCEPTED',
    })),
})
